/**
 * Створення документа «Фінансова звітність» (ч.1386): організація (лише юрособи), рік,
 * період наростаючим підсумком, набір форм (за замовчуванням — з картки організації).
 * Підпис і відправка — у Кабінеті ДПС.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  FORM_SET_TITLES, PERIOD_LABELS, financialStatements, type FinFormSet,
} from '@/api/financialStatements';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useDefaults } from '@/store/defaults';

export default function FinancialStatementCreatePage() {
  const navigate = useNavigate();
  const { availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs } =
    usePermissionsByOrg('reports.regulated.manage');
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  const eligible = availableOrgs.filter((o) => o.entity_type !== 'FOP');
  const hidden = availableOrgs.length - eligible.length;

  const now = new Date();
  const prevQ = Math.floor((now.getMonth()) / 3) || 4;
  const prevY = Math.floor(now.getMonth() / 3) === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const [orgId, setOrgId] = useState('');
  const [year, setYear] = useState(prevY);
  const [quarter, setQuarter] = useState(prevQ);
  const [formSet, setFormSet] = useState<FinFormSet | ''>('');
  const [reportType, setReportType] = useState<'REPORTING' | 'REPORTING_NEW' | 'CORRECTING'>('REPORTING');
  const [error, setError] = useState<string | null>(null);
  const effectiveOrg = orgId
    || (eligible.some((o) => o.id === defaultOrgId) ? defaultOrgId : '')
    || eligible[0]?.id || '';
  const org = eligible.find((o) => o.id === effectiveOrg);
  const cardSet = (org?.fin_report_form ?? 'MICRO') as FinFormSet;
  const effectiveSet = formSet || cardSet;

  const create = useMutation({
    mutationFn: () => financialStatements.create({
      organization_id: effectiveOrg, period_year: year, period_quarter: quarter,
      form_set: effectiveSet, report_type: reportType,
    }),
    onSuccess: (doc) => navigate(`/reports/financial/${doc.id}`),
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });

  if (!hasPermissionInAnyOrg) {
    return <div className="card">У вас немає прав складати фінансову звітність у жодній з {totalAccessibleOrgs} організацій.</div>;
  }
  return (
    <div className="card" style={{ maxWidth: 640 }}>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Нова фінансова звітність</h2>
        <button style={{ marginLeft: 'auto' }} onClick={() => navigate('/reports/financial')}>до списку</button>
      </div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', marginTop: 0 }}>
        Баланс і звіт про фінансові результати заповнюються з проводок: залишки на початок року і на
        кінець періоду, обороти наростаючим підсумком з 1 січня і за той самий період минулого року.
        Які форми подає організація — мікропідприємства, малого підприємства чи повні — з картки
        організації; тут можна обрати інакше. У повному наборі за рік додаються звіт про рух грошових
        коштів і звіт про власний капітал. Кожен рядок потім можна виправити. Підпис і відправка —
        у Журналі Кабінету ДПС.
      </p>
      {eligible.length === 0 ? (
        <div style={{ color: 'var(--badge-amber-fg)' }}>
          Серед доступних організацій немає юридичних осіб — ФОП фінансову звітність не складає.
        </div>
      ) : (
        <>
          <div className="field">
            <label>Організація{hidden > 0 ? ` (ФОП приховано: ${hidden})` : ''}</label>
            <select value={effectiveOrg} onChange={(e) => { setOrgId(e.target.value); setFormSet(''); }}>
              {eligible.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Форми</label>
            <select value={effectiveSet} onChange={(e) => setFormSet(e.target.value as FinFormSet)}>
              {(Object.keys(FORM_SET_TITLES) as FinFormSet[]).map((k) => (
                <option key={k} value={k}>{FORM_SET_TITLES[k]}{k === cardSet ? ' — як у картці' : ''}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Тип</label>
            <select value={reportType} onChange={(e) => setReportType(e.target.value as any)}>
              <option value="REPORTING">Звітна</option>
              <option value="REPORTING_NEW">Звітна нова</option>
              <option value="CORRECTING">Уточнююча</option>
            </select>
          </div>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 9' }}>
              <label>Звітний період</label>
              <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
                {[1, 2, 3, 4].map((q) => <option key={q} value={q}>{PERIOD_LABELS[q]}</option>)}
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Рік</label>
              <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
            </div>
          </div>
          {error && <div style={{ color: 'var(--badge-red-fg)', marginBottom: 8 }}>{error}</div>}
          <button className="primary" disabled={create.isPending || !effectiveOrg} onClick={() => create.mutate()}>
            створити
          </button>
        </>
      )}
    </div>
  );
}
